import { type OptionsObject, useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { type AlertColor } from '@mui/material';
import { lowerFirst } from 'lodash';
import { type TRPCError } from '@trpc/server';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import getOrganizationSyncErrorMessageKey from '@/shared/utils/organizationSyncErrors';

export type NotificationConfig = {
  message: React.ReactNode;
  variant: AlertColor;
  header?: string;
  onSuccessRoute?: string;
  options?: OptionsObject;
};

type ErrorType = Partial<TRPCError> & {
  status?: number;
  data?: {
    apiErrorCode?: string;
    apiErrorData?: { [key: string]: unknown };
    httpStatus?: number;
    errorCode?: string;
  };
  response?: {
    status?: number;
    data?: {
      apiErrorCode?: string;
      errorCode?: string;
    };
  };
};

const BAD_REQUEST = 400;
const UNPROCESSABLE_CONTENT = 422;

const isError = (error: unknown): error is ErrorType => !!error && typeof error === 'object';

const getErrorMessageKey = (error: unknown, hasErrorMessage: (key: string) => boolean) => {
  if (!isError(error)) return null;

  const errorDetails = error.data ?? error.response?.data;

  if (!errorDetails) return null;

  const status = error.data ? error.data.httpStatus ?? error.status : error.response?.status;

  if (status !== BAD_REQUEST && status !== UNPROCESSABLE_CONTENT) return null;

  return (
    [
      errorDetails.apiErrorCode,
      errorDetails.errorCode,
      status === UNPROCESSABLE_CONTENT && error.data ? error.message : undefined,
    ]
      .map((candidate) => candidate && lowerFirst(candidate))
      .find((candidate) => !!candidate && hasErrorMessage(candidate)) ?? null
  );
};

const getInterpolationValues = (error: unknown) => {
  if (!isError(error)) return undefined;

  const apiErrorData = error.data?.apiErrorData;

  if (!apiErrorData || typeof apiErrorData !== 'object') return undefined;

  const { detail, errorCode, status, title, traceId, type, ...values } = apiErrorData;

  return values;
};

export const useNotification = () => {
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const { i18n, t } = useTranslation(['common', 'errors']);
  const navigate = useNavigate();

  const sendNotification = useCallback(
    ({ message, variant, header, onSuccessRoute, options }: NotificationConfig) => {
      enqueueSnackbar(message, {
        header,
        onOpen: (id) => {
          if (onSuccessRoute) {
            navigate(onSuccessRoute);
            closeSnackbar(id);
          }
        },
        showOpenIcon: !!onSuccessRoute,
        variant,
        ...options,
      });
    },
    [closeSnackbar, enqueueSnackbar, navigate],
  );

  const getKnownErrorMessage = useCallback(
    (error: unknown) => {
      const messageKey =
        getOrganizationSyncErrorMessageKey(error) ??
        getErrorMessageKey(error, (key) => i18n.exists(`errors:${key}`));

      return messageKey
        ? t(`errors:${messageKey}`, { replace: getInterpolationValues(error) })
        : null;
    },
    [i18n, t],
  );

  const getErrorMessageFromError = useCallback(
    (error: unknown) =>
      getKnownErrorMessage(error) ?? t('errors:somethingWentWrongPleaseTryAgainLater'),
    [getKnownErrorMessage, t],
  );

  const sendDefaultError = useCallback(
    (error: unknown) => {
      const message = getErrorMessageFromError(error);

      sendNotification({
        header: t('errors:oops'),
        message,
        variant: 'error',
      });
    },
    [t, getErrorMessageFromError, sendNotification],
  );

  const sendSuccessMessage = useCallback(
    (message: React.ReactNode, onSuccessRoute?: string | null) => {
      sendNotification({
        header: t('success'),
        message,
        options: { header: t('success'), onSuccessRoute: onSuccessRoute ?? undefined },
        variant: 'success',
      });
    },
    [t, sendNotification],
  );

  return useMemo(
    () => ({
      getErrorMessageFromError,
      getKnownErrorMessage,
      sendDefaultError,
      sendNotification,
      sendSuccessMessage,
    }),
    [
      getErrorMessageFromError,
      getKnownErrorMessage,
      sendDefaultError,
      sendNotification,
      sendSuccessMessage,
    ],
  );
};

export default useNotification;
